// src/components/ui/TextAreaV2.tsx
import React, { forwardRef, TextareaHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const textareaVariants = cva(
  'flex w-full rounded-md border border-input bg-background px-4 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-y transition-all duration-150',
  {
    variants: {
      size: {
        sm: 'min-h-[80px]',
        md: 'min-h-[120px]',
        lg: 'min-h-[160px]',
      },
      variant: {
        default: '',
        error: 'border-destructive focus-visible:ring-destructive',
        success: 'border-success focus-visible:ring-success',
      },
    },
    defaultVariants: {
      size: 'md',
      variant: 'default',
    },
  }
);

export interface TextAreaV2Props
  extends TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  error?: string;
  helperText?: string;
  maxLength?: number;
  showCount?: boolean;
}

const TextAreaV2 = forwardRef<HTMLTextAreaElement, TextAreaV2Props>(
  ({
    className,
    size,
    variant,
    error,
    helperText,
    maxLength,
    showCount = false,
    value,
    ...props
  }, ref) => {
    const textareaVariant = error ? 'error' : variant;
    const currentLength = typeof value === 'string' ? value.length : 0;

    return (
      <div className="w-full">
        <textarea
          className={cn(textareaVariants({ size, variant: textareaVariant, className }))}
          ref={ref}
          maxLength={maxLength}
          value={value}
          {...props}
        />
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex-1">
            {error && (
              <p className="text-xs text-destructive">{error}</p>
            )}
            {helperText && !error && (
              <p className="text-xs text-muted-foreground">{helperText}</p>
            )}
          </div>
          {showCount && maxLength && (
            <p className={cn(
              'text-xs',
              currentLength > maxLength * 0.9 ? 'text-warning' : 'text-muted-foreground',
              currentLength >= maxLength && 'text-destructive'
            )}>
              {currentLength}/{maxLength}
            </p>
          )}
        </div>
      </div>
    );
  }
);

TextAreaV2.displayName = 'TextAreaV2';

export { TextAreaV2, textareaVariants };

